<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <form
      v-if="!isSuccess"
      id="login-form"
      class="login-form custom-form"
      novalidate
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <div
        class="text-center p-b-20"
      >
        <p
          class="subtitle p-b-20"
          v-html="$t('JOIN_ORP_INVITE_SUB_HEADER_1', { applicantName, accountName, applicantType })"
        />
        <p
          class="subtitle"
        >
          {{ $t('JOIN_PRP_INVITE_SUB_HEADER_2') }}
        </p>
      </div>
      <ValidationProvider
        v-slot="{ errors }"
        name="Password"
        rules="required|passwordLength|password"
        mode="lazy"
      >
        <fieldset
          class="login-form__field input__wrap password__wrap set__password__wrap"
          :class="{ 'error-focus': errors.length > 0 }"
        >
          <input
            id="password"
            v-model="password"
            :type="passwordType"
            name="password"
            class="login-form__field__input password-has-icon required"
            :class="{
              error: errors.length > 0,
              valid: password.length > 0 && errors.length == 0,
            }"
            required
          >
          <label
            v-t="'INPUT_NEW_PASSWORD'"
            for="password"
            class="login-form__field__label"
          />
          <a
            class="icon--password fa fa-eye"
            @click="showPassword('passwordType')"
          />
          <div class="error-tooltip__wrap">
            <h4 
              v-t="'INPUT_PASSWORD_ERROR'"
              class="menu-link up-text error-tooltip__content"
            />
            <p 
              class="description xxs-visible" 
            >
              {{ errors[0] }}
            </p>
          </div>
        </fieldset>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        name="Password Confirmation"
        rules="required|passwordMatch:@Password"
        mode="lazy"
      >
        <fieldset
          class="login-form__field input__wrap password__wrap set__password__wrap"
          :class="{ 'error-focus': errors.length > 0 }"
        >
          <input
            id="confirmPassword"
            v-model="confirmPassword"
            :type="confirmPasswordType"
            name="confirmPassword"
            class="login-form__field__input password-has-icon required"
            :class="{
              error: errors.length > 0,
              valid: confirmPassword.length > 0 && errors.length == 0,
            }"
            required
          >
          <label
            v-t="'INPUT_CONFIRM_PASSWORD'"
            for="confirmPassword"
            class="login-form__field__label"
          />
          <a
            class="icon--password fa fa-eye"
            @click="showPassword('confirmPasswordType')"
          />
          <div class="error-tooltip__wrap">
            <h4 
              v-t="'INPUT_PASSWORD_ERROR'"
              class="menu-link up-text error-tooltip__content"
            />
            <p 
              class="description xxs-visible" 
            >
              {{ errors[0] }}
            </p>
          </div>
        </fieldset>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        :rules="{ required: { allowFalse: false } }"
      >
        <fieldset
          class="login-form__field login-form__field--checkbox checkbox__wrap"
        >
          <input
            id="tos.checkbox"
            v-model="tosAccepted"
            type="checkbox"
            name="tos.checkbox"
            class="login-form__field__input--checkbox"
          >
          <label
            for="tos.checkbox"
            class="login-form__field__label login-form__field__label--checkbox"
          >
            {{ $t('JOIN_INVITE_TERMS_PREFIX') }} 
            <u> <a
              href="http://www.ringba.com/terms"
              class="home_link"
              target="_BLANK"
            > {{ $t('JOIN_INVITE_TERMS_LINK') }} 
            </a></u>  
            {{ $t('JOIN_INVITE_TERMS_AND') }}
            <u>
              <a
                href="http://www.ringba.com/privacy"
                class="home_link"
                target="_BLANK"
              > {{ $t('JOIN_INVITE_PRIVACY_POLICY') }}
              </a> </u>
          </label>
          <transition name="backend-error-fade">
            <div
              v-show="errors.length > 0"
              class="text-center p-t-5"
            >
              <p
                v-t="'TERMS_EROR'"
                class="text-danger"
              />
            </div>
          </transition>
        </fieldset>
      </ValidationProvider>
      <fieldset class="login-form__field login-form__field--button">
        <SubmitButton :pending="isPending">
          {{ $t('JOIN_PRP_INVITE_BUTTON') }} 
        </SubmitButton>
      </fieldset>
      <transition name="backend-error-fade">
        <div
          v-show="showErrorMsg"
          class="text-center p-t-5"
        >
          <p
            class="text-danger"
          >
            {{ actionStatus.error }}
          </p>
        </div>
      </transition>
    </form>
    <div
      v-if="isSuccess"
      class="success-message text-center"
    >
      <h1
        v-t="$t('JOIN_ORP_INVITE_SUCCESS', { applicantType })"
        class="title md-title"
      />
      <p
        v-t="'JOIN_INVITE_SUCCESS_SUBHEADER'"
        class="subtitle"
      />
      <a
        v-t="'BACK_LOGIN_LINK'"
        href="#"
        class="button button--transparent-grad pulse m-t-20"
        @click="goToLogin"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import messages from '../locales';
import { STATUS_PENDING, STATUS_ERROR, STATUS_SUCCESS } from '@/store/helpers';
import SubmitButton from '@/modules/auth/components/SubmitButton';
import { $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';

export default {
  components: {
    SubmitButton,
  },
  props: {
    actionStatus: {
      type: Object,
      default() {
        return {};
      },
    },
    applicantName: {
      type: String,
      default() {
        return '';
      },
    },
    accountName: {
      type: String,
      default() {
        return '';
      },
    },
    applicantType: {
      type: String,
      default() {
        return '';
      },
    }
  },
  data: function () {
    return {
      password: '',
      confirmPassword: '',
      passwordType: 'password',
      confirmPasswordType: 'password',
      tosAccepted: false,
    };
  },
  computed: {
    isPending() {
      return this.actionStatus.status === STATUS_PENDING;
    },
    isSuccess() {
      return this.actionStatus.status === STATUS_SUCCESS;
    },
    showErrorMsg() {
      return this.actionStatus.status === STATUS_ERROR && this.actionStatus.error.length > 0;
    }
  },
  methods: {
    showPassword(passwordField) {
      if (this[passwordField] === 'password') {
        this[passwordField] = 'text';
      } else {
        this[passwordField] = 'password';
      }
    },
    onSubmit() {
      const { password, confirmPassword } = this;
      this.$emit('join-orp-invite', { password, confirmPassword });
    },
    goToLogin() {
      $ngStateService.go('login');
    }
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>

<style>
</style>